import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─── Token Management (localStorage + axios default header) ───
const TOKEN_KEY = 'konnect_access_token';
const REFRESH_KEY = 'konnect_refresh_token';

function setStoredToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
}

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function clearStoredTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  delete axios.defaults.headers.common['Authorization'];
}

// Initialize axios with stored token on load
const existingToken = getStoredToken();
if (existingToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;
}

// IMPORTANT: Do NOT use withCredentials (cookies) for cross-origin.
// Token is sent via Authorization header from localStorage.
axios.defaults.withCredentials = false;

// ─── 401 Interceptor: Auto-refresh on expired access token ───
let _isRefreshing = false;
let _failedQueue = [];
const _processQueue = (error, token = null) => {
  _failedQueue.forEach(prom => { error ? prom.reject(error) : prom.resolve(token); });
  _failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/refresh')) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }
      originalRequest._retry = true;
      _isRefreshing = true;
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) {
        try {
          // Hard 5s timeout so a flaky refresh endpoint can NEVER hang the
          // app's splash forever (root cause of the infinite "Loading…" bug).
          const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
            headers: { 'Authorization': `Bearer ${refreshToken}` },
            timeout: 5000,
          });
          if (data.token) {
            setStoredToken(data.token);
            _processQueue(null, data.token);
            originalRequest.headers['Authorization'] = `Bearer ${data.token}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          _processQueue(refreshError);
          // Refresh failed → clear stale tokens so subsequent requests don't
          // keep triggering the same refresh-and-fail loop.
          clearStoredTokens();
        } finally {
          _isRefreshing = false;
        }
      } else {
        // No refresh token → original 401 should propagate cleanly.
        _isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper to format API error details
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null); // "View As" role for super_admin
  // "Show all features" toggle — when ON, Super Admins / Country Operators see
  // every menu item they have role-access to (subscriber stuff included).
  // OFF (default) shows only items tagged with their audience for a clean
  // monitoring view. Persisted to localStorage so the choice sticks.
  const [showAllFeatures, _setShowAllFeatures] = useState(() => {
    try { return localStorage.getItem('konnect_show_all_features') === '1'; } catch { return false; }
  });
  const setShowAllFeatures = (v) => {
    _setShowAllFeatures(v);
    try { localStorage.setItem('konnect_show_all_features', v ? '1' : '0'); } catch { /* private mode */ }
  };

  const checkAuth = useCallback(async () => {
    // GUARANTEED safety net — no matter what the API/interceptor chain does,
    // after 10s we force the splash to clear so users always see SOMETHING.
    const splashKill = setTimeout(() => setLoading(false), 10000);
    try {
      const token = getStoredToken();
      if (!token) {
        setUser(false);
        return;
      }
      // Hard timeout — if /api/auth/me hangs (stale service worker on a new PC,
      // network blip, etc.), bail out after 8s instead of stranding the user on
      // the splash screen forever.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { signal: controller.signal });
        setUser(data);
      } catch (error) {
        // Token might be expired — try refresh
        try {
          const refreshToken = localStorage.getItem(REFRESH_KEY);
          if (refreshToken) {
            const { data: refreshData } = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
              headers: { 'Authorization': `Bearer ${refreshToken}` },
              timeout: 5000,
            });
            if (refreshData.token) {
              setStoredToken(refreshData.token);
              const { data } = await axios.get(`${API_URL}/api/auth/me`, { timeout: 5000 });
              setUser(data);
              return;
            }
          }
        } catch { /* refresh failed */ }
        clearStoredTokens();
        setUser(false);
      } finally {
        clearTimeout(timeoutId);
      }
    } finally {
      clearTimeout(splashKill);
      setLoading(false);
    }
  }, []);

  // Separate refreshUser that does NOT logout on failure
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch {
      // Silent — don't logout, just keep current user state
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ─── Auto-refresh token every 50 minutes (before 60-min expiry) ───
  const _refreshIntervalRef = { current: null };
  const _startAutoRefresh = () => {
    if (_refreshIntervalRef.current) clearInterval(_refreshIntervalRef.current);
    _refreshIntervalRef.current = setInterval(async () => {
      try {
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (!refreshToken) return;
        const { data: refreshData } = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (refreshData.token) {
          setStoredToken(refreshData.token);
        }
      } catch { /* silent — will retry next interval */ }
    }, 50 * 60 * 1000); // Every 50 minutes
  };

  // Start auto-refresh if we already have a refresh token
  useEffect(() => {
    const rt = localStorage.getItem(REFRESH_KEY);
    if (rt && user) _startAutoRefresh();
    return () => { if (_refreshIntervalRef.current) clearInterval(_refreshIntervalRef.current); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    // Normalize inputs — browser autofill sometimes appends whitespace/newline
    // that silently breaks bcrypt exact-match on the server.
    email = (email || '').trim().toLowerCase();
    password = (password || '').replace(/^\s+|\s+$/g, '');

    // Auth0/Firebase-style retry: 3 attempts, exponential backoff (0s, 2s, 4s),
    // 30s per-attempt timeout to accommodate Kubernetes / Passenger cold starts.
    // NEVER retry on 4xx — those are real (wrong pw, locked, validation).
    // Retry ONLY on network failure (no response) or 5xx (server hiccup).
    const attempts = [0, 2000, 4000];
    const attemptOnce = () => axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { timeout: 30000 },
    );

    let lastError = null;
    for (let i = 0; i < attempts.length; i++) {
      if (attempts[i] > 0) {
        await new Promise((r) => setTimeout(r, attempts[i]));
      }
      try {
        const { data } = await attemptOnce();

        // SUCCESS — persist tokens, hydrate user, start refresh loop.
        if (data.token) setStoredToken(data.token);
        if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
        setUser(data);
        _autoDetectLocale();
        _startAutoRefresh();

        // Optional preferred-host redirect (for whitelabel subscribers).
        try {
          const { data: ph } = await axios.get(`${API_URL}/api/auth/preferred-host`, { timeout: 5000 });
          const here = window.location.host;
          const want = ph?.preferred_host;
          const mustEnforce = ph?.role === 'super_admin'
            || (ph?.role !== 'country_operator' && ph?.enforced);
          if (want && mustEnforce && want !== here) {
            window.location.href = `https://${want}/login?token=${encodeURIComponent(data.token)}`;
            return { success: true, data, redirected: true };
          }
        } catch { /* no preferred host info — proceed normally */ }

        return { success: true, data };
      } catch (err) {
        lastError = err;
        const status = err.response?.status;
        // Fatal errors — never retry. Bubble up immediately.
        if (status === 401 || status === 403 || status === 422 || status === 429) {
          break;
        }
        // Otherwise (network fail, 5xx, timeout) → try again if attempts remain.
      }
    }

    // We're out of attempts. Map the error to a clean, actionable message.
    const status = lastError?.response?.status;
    let message;
    if (status === 401) {
      message = "Wrong email or password. Please try again or use \"Forgot password\".";
    } else if (status === 429) {
      const detail = lastError?.response?.data?.detail;
      message = typeof detail === "string" ? detail : "Too many attempts. Please wait 15 minutes and try again.";
    } else if (status === 422) {
      message = formatApiErrorDetail(lastError?.response?.data?.detail);
    } else if (status === 403) {
      message = formatApiErrorDetail(lastError?.response?.data?.detail) || "Access denied.";
    } else if (status >= 500) {
      message = "Our servers hit a bump. We tried a few times — please try again in a moment.";
    } else if (lastError?.code === 'ECONNABORTED' || lastError?.message?.includes('timeout')) {
      message = "The server took too long to respond. It may be warming up — please try again.";
    } else {
      // Truly no response = network offline or server unreachable.
      message = "We can't reach the server right now. Please check your connection or try again in a moment.";
    }
    return { success: false, error: message };
  };

  // Fire-and-forget locale detection
  const _autoDetectLocale = async () => {
    try {
      const { data: gs } = await axios.get(`${API_URL}/api/settings/global`);
      if (gs.timezone && gs.language && gs.country) return; // already configured
      const detected = {};
      if (!gs.timezone) detected.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      if (!gs.language) detected.language = (navigator.language || 'en').split('-')[0];
      if (Object.keys(detected).length > 0) {
        await axios.put(`${API_URL}/api/settings/global`, { ...gs, ...detected });
      }
    } catch { /* silent — supplementary feature */ }
  };

  const register = async (userData) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        userData
      );
      if (data.token) {
        setStoredToken(data.token);
      }
      if (data.refresh_token) {
        localStorage.setItem(REFRESH_KEY, data.refresh_token);
      }
      setUser(data);
      _startAutoRefresh();
      return { success: true, data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredTokens();
      setUser(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const completeOnboarding = async (onboardingData) => {
    try {
      await axios.post(`${API_URL}/api/auth/onboarding`, onboardingData);
      setUser(prev => ({ ...prev, onboarding_completed: true }));
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  // The effective role used for UI rendering (navigation, module visibility)
  const effectiveRole = viewAsRole || user?.role;

  // Memoize the context value so consumers don't re-render whenever
  // AuthProvider re-renders for unrelated reasons. Identity-stable across
  // renders unless one of the actual deps changes.
  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    completeOnboarding,
    refreshUser,
    viewAsRole,
    setViewAsRole,
    effectiveRole,
    isViewingAs: !!viewAsRole && viewAsRole !== user?.role,
    showAllFeatures,
    setShowAllFeatures,
  // login/register/logout/etc. are recreated each render too — list user/loading/viewAsRole
  // which are the values that actually flip. Functions are read inside via closure.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading, viewAsRole, effectiveRole, showAllFeatures]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
